import { useEffect, useState, useRef } from 'react';
import { State, process } from '@progress/kendo-data-query';
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridToolbar,
  GridDataStateChangeEvent,
  GridSelectionChangeEvent,
  GridHeaderSelectionChangeEvent,
  getSelectedState,
  GridExpandChangeEvent,
  GridRowProps,
  GridColumnReorderEvent,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { mapTree } from '@progress/kendo-react-common';
import { getter } from '@progress/kendo-react-common';
import Button from '@mui/material/Button';
import { Input } from '@progress/kendo-react-inputs';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { LoadingIndicator } from '../loading-indicator.component';
import { ColumnMenu } from './columnMenu';
import { GridColumnConfig } from '../../models/gridCoumnConfig';
import { MutationKeyConfig, QueryKeyConfig } from '../../api/api-helper';
import { GridCommandCell } from './grid-command-cell';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { Icons } from '../../assets/icons';
import { useNavigate } from 'react-router-dom';
import { GuardedComponent } from '../guarded-component';
import { UserPermissionName } from '../../models/user-permission-name';
import { CustomCellSelector } from './custom-grid-selector';
import { IconButton, Tooltip, Typography } from '@mui/material';
import { ConfirmationDialog } from '../../features/units/unit-details/proctor-result/confirmation.dialog';
import ConfirmDialog from '../confirm-dialog';
import { GridPDFExport, PDFExport } from '@progress/kendo-react-pdf';
import { messages } from '@progress/kendo-react-inputs/messages';
import { removeFilterFromState, RenderGridFilterLabel, reorderColumns } from './util';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import dayjs from 'dayjs';

interface GridActionPermission {
  view?: UserPermissionName;
  edit?: UserPermissionName;
  delete?: UserPermissionName;
  download?: UserPermissionName;
  export?: UserPermissionName;
  pdfExport?: UserPermissionName;
}

interface SiTrackGridProp {
  columns: GridColumnConfig[];
  dataItemKey: string;
  dataItems?: any[];
  queryConfig?: QueryKeyConfig;
  isBusy?: boolean;
  showWithoutSorting?: boolean; //to display data same order from api
  isShowUncheckConfirmation?: boolean;
  isDiableHeaderCell?: boolean;
  preventCheckAndUnCheckOnHeaderelectionChange?: any[];
  DetailsComponent?: any;
  CustomComponent?: any;
  selectedField?: string;
  selectionMode?: string;
  actionPermissions?: GridActionPermission;
  defaultGroup?: any[];
  pdfDownload?: boolean;
  optionWidth?: string;
  reorderable?: boolean;
  fileTitle?: string;
  viewRoute?: (data: any) => string;
  viewPopup?: (data: any) => void;
  handleRowClick?: (data: any) => void;
  customCell?: (field: any) => ((dataItem: any) => JSX.Element) | undefined;
  onGridDataChange?: (data: any[]) => void;
  onEdit?: (data: any) => void;
  onView?: (data: any) => void;
  onDelete?: (data: any) => void;
  onSelectedItems?: (selectedItems: any[], isChecked: boolean) => void;
  onEditComplete?: (data: any) => void;
  onDeletedItems?: (deletedItems: any[]) => void;
  deleteMutationConfig?: (item: any) => MutationKeyConfig;
  onDownload?: (data: any) => void;
  footerCell?: any;
  handleResetLayout?: () => void;
  handleSearch?: (searchTerm: string) => void;
  handleSaveLayout?: (gridDataState: State, gridColumns: GridColumnConfig[]) => void;
  currentPageSize?: number;
  isSaveLayout?: boolean;
  gridConfigFromApi?:
  | {
    dataState: State | undefined;
    columns: any[] | undefined;
  }
  | undefined;
  rowRender?: (trElement: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => React.ReactNode;
  conditionalCellRender?: (
    defaultRendering: React.ReactElement<HTMLTableCellElement> | null,
    props: GridCellProps
  ) => React.ReactElement<HTMLTableCellElement> | React.ReactElement<HTMLTableCellElement>[] | null;
}

interface MergedItem {
  [key: string]: any;
}

const mergeDataItems = (array1: any[], array2: any[], key: string): MergedItem[] => {
  const mappedArray = new Map();
  Array.isArray(array1) &&
    array1?.forEach((item) => {
      mappedArray.set(item[key], item);
    });

  Array.isArray(array2) &&
    array2?.forEach((item) => {
      mappedArray.set(item[key], item);
    });

  return Array.from(mappedArray.values());
};

export const SiTrackGridWithGroupSelect = ({
  columns,
  dataItemKey,
  dataItems,
  queryConfig,
  isBusy,
  isShowUncheckConfirmation,
  preventCheckAndUnCheckOnHeaderelectionChange,
  selectedField,
  selectionMode,
  DetailsComponent,
  CustomComponent,
  actionPermissions,
  defaultGroup,
  showWithoutSorting,
  footerCell,
  pdfDownload,
  optionWidth,
  fileTitle,
  viewRoute,
  viewPopup,
  deleteMutationConfig,
  customCell,
  onGridDataChange,
  onEdit,
  onView,
  onDelete,
  onSelectedItems,
  onDeletedItems,
  onEditComplete,
  onDownload,
  rowRender,
  conditionalCellRender,
  currentPageSize,
  handleRowClick,
  handleSearch,
  handleSaveLayout,
  isDiableHeaderCell,
  handleResetLayout,
  isSaveLayout,
  reorderable,
  gridConfigFromApi,
}: SiTrackGridProp) => {
  const _export: any = useRef(null);
  let gridPDFExport: GridPDFExport | null;
  const navigate = useNavigate();
  const [dataState, setDataState] = useState<State>({
    take: currentPageSize ? currentPageSize : 50,
    skip: 0,
    group: defaultGroup,
  });
  const [collapsedGroups, setCollapsedGroups]: any = useState([]);
  const [completeData, setCompleteData] = useState<any[]>(dataItems ? dataItems : []);
  const [deletedItems, setDeletedItems] = useState<any[]>([]);
  const [stateColumns, setStateColumns] = useState<GridColumnConfig[]>(
    columns?.map((col) => ({ ...col, columnShow: true }))
  );
  const [showResetFilter, setShowResetFilter] = useState<boolean>(false);
  const [isPageChange, setIsPageChange] = useState<boolean>(false);
  const [isConfirmation, setIsConfirmation] = useState<boolean>(false);
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => { });
  const [selectedState, setSelectedState] = useState<{
    [id: string]: boolean | number[];
  }>({});
  const { data, isLoading, isFetching } = useLoadData<{
    data: ApiResponse<any>[];
    totalRecords: number;
  }>(queryConfig, !!queryConfig);

  const SELECTED_FIELD: string = selectedField ? selectedField : '';
  const idGetter = getter(dataItemKey);

  useEffect(() => {
    if (queryConfig && queryConfig?.isEnabled === false) {
      setCompleteData([]);
    }
  }, [queryConfig]);

  useEffect(() => {
    const mergedData = mergeDataItems(data?.data ?? [], dataItems ?? [], dataItemKey).filter(
      (d) => !deletedItems.some((r) => r[dataItemKey] === d[dataItemKey])
    );
    if (!showWithoutSorting) {
      setCompleteData(mergedData);
    } else {
      setCompleteData(dataItems ? dataItems : []);
    }
    if (selectedField) {
      const selectedItemState = mergedData.reduce((state: any, data: any) => {
        return { ...state, [data[dataItemKey]]: data[SELECTED_FIELD] };
      }, {});
      setSelectedState(selectedItemState);
    }
  }, [data, dataItems, deletedItems]);

  useEffect(() => {
    onGridDataChange?.(completeData);

    if (!isPageChange) {
      setDataState({ ...dataState, skip: 0 });
    }
    // setIsPageChange(false);
  }, [completeData]);

  useEffect(() => {
    const headerCheckbox = document.querySelector('.k-grid-header .k-checkbox');
    if (headerCheckbox instanceof HTMLElement && isDiableHeaderCell) {
      headerCheckbox.style.display = 'none';
    }
  }, []);

  const handleDataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    const newSelectedState = getSelectedState({
      event,
      selectedState: selectedState,
      dataItemKey,
    });

    const currentSelectedState = selectedState[idGetter(event.dataItem)];

    const isUnchecked = currentSelectedState && !newSelectedState[idGetter(event.dataItem)];

    if (isUnchecked && isShowUncheckConfirmation) {
      setConfirmAction(() => () => {
        setIsConfirmation(false);
        setIsPageChange(true);
        const updatedData = completeData.map((d) => ({ ...d, [SELECTED_FIELD]: newSelectedState[idGetter(d)] }));

        setSelectedState(newSelectedState);
        setCompleteData(updatedData);
        onSelectedItems?.(
          updatedData.filter((d) => d[SELECTED_FIELD]),
          false
        );
      });
      setIsConfirmation(true);
    } else {
      setIsPageChange(true);
      const updatedData = completeData.map((d) => ({ ...d, [SELECTED_FIELD]: newSelectedState[idGetter(d)] }));
      setSelectedState(newSelectedState);

      setCompleteData(updatedData);
      onSelectedItems?.(
        updatedData.filter((d) => d[SELECTED_FIELD]),
        true
      );
    }

    if (event?.mode === 'single' && currentSelectedState) {
      const selectedRowId = idGetter(event.dataItem); // Get the ID of the clicked row
      const isCurrentlySelected = selectedState[selectedRowId]; // Check if the row is currently selected

      const updatedData = completeData.map((item) => {
        // Toggle the selection state for the clicked row
        if (idGetter(item) === selectedRowId) {
          return { ...item, [SELECTED_FIELD]: !isCurrentlySelected };
        }
        // Unselect all other rows
        return { ...item, [SELECTED_FIELD]: false };
      });

      // Update the selected state map
      const updatedSelectedState = {
        ...selectedState,
        [selectedRowId]: !isCurrentlySelected, // Toggle the clicked row's selection
      };

      setSelectedState(updatedSelectedState);
      setCompleteData(updatedData);

      // Pass the newly selected items (if any) to the callback
      onSelectedItems?.(
        updatedData.filter((item) => item[SELECTED_FIELD]), // Only rows with SELECTED_FIELD === true
        !isCurrentlySelected // Indicates whether an item was selected or deselected
      );
    }
  };

  const handleEditComplete = (value: any) => {
    const updatedData = mergeDataItems(completeData, [value], dataItemKey);
    setCompleteData(updatedData);
    onEditComplete?.(value);
  };
  const [headerChecked, setHeaderChecked] = useState(false);

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent) => {
    const checkboxElement: any = event.syntheticEvent.target;
    const checked = checkboxElement.checked;
    const newSelectedState: any = {};
    setHeaderChecked(checked);
    if (!checked) {
      setCheckedGroupIDs([]);
    } else {
      if (dataState?.group?.length) {
        const updatedCompleteData = [...completeData];
        const field = dataState?.group?.length ? dataState.group[0]?.field : '';
        const uniqueNames = Array.from(new Set(updatedCompleteData.map((item) => item[field])));
        setCheckedGroupIDs(uniqueNames);
      }
    }
    // if (!preventCheckAndUnCheckOnHeaderelectionChange) {
    //   Array.isArray(completeData) &&
    //     completeData?.forEach((item) => {
    //       newSelectedState[idGetter(item)] = checked;
    //     });
    // } else {
    //   Array.isArray(completeData) &&
    //     completeData.forEach((item) => {
    //       if (!item[preventCheckAndUnCheckOnHeaderelectionChange]) {
    //         newSelectedState[idGetter(item)] = checked;
    //       } else {
    //         newSelectedState[idGetter(item)] = item[SELECTED_FIELD];
    //       }
    //     });
    // }

    if (
      !Array.isArray(preventCheckAndUnCheckOnHeaderelectionChange) ||
      preventCheckAndUnCheckOnHeaderelectionChange.length === 0
    ) {
      // No prevention logic, just select/deselect all items
      Array.isArray(completeData) &&
        completeData.forEach((item) => {
          newSelectedState[idGetter(item)] = checked;
        });
    } else {
      // Loop through the completeData and apply logic based on the array of prevent fields
      Array.isArray(completeData) &&
        completeData.forEach((item) => {
          // If any of the fields in preventCheckAndUnCheckOnHeaderelectionChange array matches or mappingID !== 0, prevent selection
          const isPrevented = preventCheckAndUnCheckOnHeaderelectionChange.some((field) => item[field]);
          // || item.mappingID !== 0;

          if (!isPrevented) {
            newSelectedState[idGetter(item)] = checked;
          } else {
            newSelectedState[idGetter(item)] = item[SELECTED_FIELD]; // Keep existing selection state
          }
        });
    }

    if (!checked && isShowUncheckConfirmation) {
      setConfirmAction(() => () => {
        setIsConfirmation(false);
        const updatedData = completeData.map((d) => ({ ...d, [SELECTED_FIELD]: newSelectedState[idGetter(d)] }));
        setCompleteData(updatedData);
        setSelectedState(newSelectedState);
        onSelectedItems?.(
          updatedData.filter((d) => d[SELECTED_FIELD]),
          false
        );
      });
      setIsConfirmation(true);
    } else {
      setIsPageChange(true);
      const updatedData = completeData.map((d) => ({ ...d, [SELECTED_FIELD]: newSelectedState[idGetter(d)] }));
      setSelectedState(newSelectedState);
      setCompleteData(updatedData);
      onSelectedItems?.(
        updatedData.filter((d) => d[SELECTED_FIELD]),
        true
      );
    }
    // const updatedData = completeData?.map((d) => ({ ...d, [SELECTED_FIELD]: newSelectedState[idGetter(d)] }));
    // setCompleteData(updatedData);
    // setSelectedState(newSelectedState);
    // onSelectedItems?.(updatedData.filter((d) => d[SELECTED_FIELD]));
  };

  const onColumnsSubmit = (columnsState: any) => {
    setStateColumns(columnsState);
    isSaveLayout && handleSaveLayout?.(dataState, columnsState);
  };

  const searchFilter = (value: string): any => {
    return {
      logic: 'or',
      filters: columns
        .filter((c) => c.searchable)
        .map((c) => ({
          field: c.field,
          operator: 'contains',
          value: value,
        })),
    };
  };

  const handleSearchDataChange = (e: any) => {
    !handleSearch
      ? setDataState({ ...dataState, filter: searchFilter(e.target.value), skip: 0 })
      : handleSearch?.(e.target.value);
  };

  const flattenGroupedData = (groupedData: any[]): any[] => {
    const result: any[] = [];

    const flatten = (data: any[]) => {
      data.forEach((item) => {
        if (item.items) {
          flatten(item.items);
        } else {
          result.push(item);
        }
      });
    };

    flatten(groupedData);
    return result;
  };

  const handleExportExcelCick = () => {
    if (_export.current !== null) {
      if (dataState?.group?.length === 0) {
        _export.current.save(process(completeData, { ...dataState, take: 100000, skip: 0 }));
      } else {
        const processedData = process(completeData, { ...dataState, take: 100000, skip: 0 }).data;

        const flattenedData = flattenGroupedData(processedData);

        _export.current.save(flattenedData);
      }
    }
  };

  const handleExportPDFCick = () => {
    if (gridPDFExport) {
      let processedData: any;
      if (dataState?.group?.length === 0) {
        processedData = process(completeData, { ...dataState, take: 100000, skip: 0 });
      } else {
        processedData = process(completeData, { ...dataState, take: 100000, skip: 0 }).data;
      }
      const flattenedData = flattenGroupedData(processedData);
      gridPDFExport.save(flattenedData);
    }
  };

  const handleDeleteComplete = (value: any) => {
    if (value) {
      setDeletedItems([...deletedItems, value]);
      onDeletedItems?.([...deletedItems, value]);
    }
  };

  // const expandChange = (event: GridExpandChangeEvent) => {
  //   if (!event.dataItem.aggregates) {
  //     const newData: any = completeData.map((item: any) => {
  //       if (item[dataItemKey] === event.dataItem[dataItemKey]) {
  //         item.expanded = !event.dataItem.expanded;
  //       }

  //       return item;
  //     });
  //     setCompleteData(newData);
  //   } else {
  //     const groupID = event.dataItem.field + '_' + event.dataItem.value;
  //     console.log(55, event.dataItem);
  //     if (!collapsedGroups.includes(groupID)) {
  //       setCollapsedGroups([...collapsedGroups, groupID]);
  //     } else {
  //       setCollapsedGroups(collapsedGroups.filter((gr: any) => gr !== groupID));
  //     }
  //   }
  // };

  const expandChange = (event: GridExpandChangeEvent) => {
    if (!event.dataItem.aggregates) {
      const newData: any = completeData.map((item: any) => {
        if (item[dataItemKey] === event.dataItem[dataItemKey]) {
          item.expanded = !event.dataItem.expanded;
        }

        return item;
      });
      setCompleteData(newData);
    } else {
      const groupID = event.dataItem.field + '_' + event.dataItem.value?.props?.children[0];

      if (!collapsedGroups.includes(groupID)) {
        setCollapsedGroups([...collapsedGroups, groupID]);
      } else {
        setCollapsedGroups(collapsedGroups.filter((gr: any) => gr !== groupID));
      }
    }
  };

  // const handleGroupState = (props: any) => {
  //   return {
  //     data: mapTree(props.data, 'items', (group) => {
  //       if (!group.aggregates) {
  //         return group;
  //       }
  //       console.log(group.value);
  //       const groupID = group.field + '_' + group.value;
  //       return { ...group, expanded: !collapsedGroups.includes(groupID) };
  //     }),
  //     total: props.total,
  //   };
  // };

  const handleGroupState = (props: any) => {
    return {
      data: mapTree(props.data, 'items', (group) => {
        if (!group.aggregates) {
          return group;
        }
        const groupID = group.field + '_' + group.value;

        // Add a checkbox to the group
        const checkbox = (
          <input
            type="checkbox"
            onChange={(e) => handleCheckboxChange(e, group, props)}
            checked={checkedGroupIDs.includes(group.value)}
          />
        );

        return {
          ...group,
          expanded: !collapsedGroups.includes(groupID),
          value: (
            <div className="chkwithTitle">
              {group.value}
              {selectedField && checkbox}
            </div>
          ),
        };
      }),
      total: props.total,
    };
  };

  //   const [test, setTest] = useState(handleGroupState(process(completeData, dataState)));
  const [checkedGroupIDs, setCheckedGroupIDs] = useState<string[]>([]);

  function findValuesByKey(data: any, searchValue: string) {
    const result: any[] = [];

    function traverse(items: any) {
      for (const item of items) {
        if (item.value === searchValue) {
          collectValues(item);
          return;
        } else if (item.items && item.items.length > 0) {
          traverse(item.items);
        }
      }
    }

    function collectValues(item: any) {
      if (item.value) {
        result.push(item.value);
      }
      if (item.items && item.items.length > 0) {
        // item?.item?.forEach((element: any) => {
        //   collectValues(element);
        // });
        collectValues(item.items[0]);
      }
    }

    traverse(data);
    return result;
  }

  const handleAddOrRemoveSubGroup = (
    event: React.ChangeEvent<HTMLInputElement>,
    props: any,
    group: { value: string; field: string }
  ) => {
    const result = findValuesByKey(props?.data, group?.value);
    setCheckedGroupIDs((prevCheckedGroupIDs) => {
      if (event.target.checked) {
        // Add all `result` items to checkedGroupIDs
        const updatedCheckedGroupIDs = [...prevCheckedGroupIDs, ...result];
        return Array.from(new Set(updatedCheckedGroupIDs)); // Ensure no duplicates
      } else {
        // Remove all `result` items from checkedGroupIDs
        const updatedCheckedGroupIDs = prevCheckedGroupIDs.filter((id) => !result.includes(id));
        return updatedCheckedGroupIDs;
      }
    });
  };

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    group: { value: string; field: string },
    props: any
  ) => {
    let updatedCompleteData = [...completeData];
    handleAddOrRemoveSubGroup(event, props, group);
    if (event.target.checked) {
      updatedCompleteData = updatedCompleteData.map((dataItem: any) =>
        group.value === dataItem[group?.field] ? { ...dataItem, isSelected: true } : dataItem
      );
    } else {
      updatedCompleteData = updatedCompleteData.map((dataItem: any) =>
        group.value === dataItem[group?.field] ? { ...dataItem, isSelected: false } : dataItem
      );
    }
    setIsPageChange(true);
    onSelectedItems?.(
      updatedCompleteData.filter((d) => d[SELECTED_FIELD]),
      false
    );
    const allSelected = updatedCompleteData.every((item) => item[SELECTED_FIELD]);
    setHeaderChecked(allSelected);
    setCompleteData(updatedCompleteData);
    // setCheckedGroupIDs((prevCheckedGroupIDs) => {
    //   if (event.target.checked) {
    //     const updatedCheckedGroupIDs = [...prevCheckedGroupIDs, group.value];

    //     return updatedCheckedGroupIDs;
    //   } else {
    //     return prevCheckedGroupIDs.filter((id) => id !== group.value);
    //   }
    // });
  };

  const handleGroupChange = (e: any) => {
    let updatedCompleteData = [...completeData];

    // Reset the 'isSelected' field to false for all data items
    updatedCompleteData = updatedCompleteData.map((dataItem: any) => ({
      ...dataItem,
      isSelected: false,
    }));

    // Trigger the 'onSelectedItems' callback with the filtered data
    onSelectedItems?.(
      updatedCompleteData.filter((d) => d[SELECTED_FIELD]),
      false
    );

    // Update the state with the new completeData and group change
    setCompleteData(updatedCompleteData);
    setDataState({
      ...dataState,
      group: e.group.length > 0 ? [e.group[!dataState?.group?.length ? 0 : dataState?.group?.length]] : [], // Keep only the first group or clear if empty
    });

    setHeaderChecked(false);
    setCheckedGroupIDs([]);
  };

  const handleViewClick = (data: any) => {
    if (viewRoute) {
      const route = viewRoute(data);
      navigate(route);
    }
    if (viewPopup) viewPopup(data);
  };

  const isActionColumnVisible = onEdit || onView || onDownload || CustomComponent || onDelete || deleteMutationConfig;

  const handleClose = () => {
    setIsConfirmation(false);
  };

  const handleConfirm = () => {
    confirmAction();
  };

  const handleSelectionChangeWithGroup = (event: GridSelectionChangeEvent) => {
    const newSelectedState = getSelectedState({
      event,
      selectedState: selectedState,
      dataItemKey,
    });
    setIsPageChange(true);
    // const updatedData = completeData.map((d) => ({ ...d, [SELECTED_FIELD]: !event?.dataItem[SELECTED_FIELD] }));
    const updatedData = completeData.map((d) =>
      d[dataItemKey] === event?.dataItem[dataItemKey] ? { ...d, [SELECTED_FIELD]: !event?.dataItem[SELECTED_FIELD] } : d
    );

    setCompleteData(updatedData);
    setSelectedState(newSelectedState);
    onSelectedItems?.(
      updatedData.filter((d) => d[SELECTED_FIELD]),
      true
    );
    const allSelected = updatedData.every((item) => item[SELECTED_FIELD]);
    setHeaderChecked(allSelected);

    if (dataState?.group?.length) {
      const field = dataState?.group?.length ? dataState.group[0]?.field : '';
      const value = event?.dataItem[dataState?.group?.length ? dataState.group[0]?.field : ''];
      handleCheckOrUncheckparentBasedOnChild(field, value, updatedData);
    }
  };

  const handleCheckOrUncheckparentBasedOnChild = (field: string, value: string, updatedData: any[]) => {
    const allSelected = updatedData?.filter((d) => d[field] === value).every((item) => item[SELECTED_FIELD]);

    setCheckedGroupIDs((prevCheckedGroupIDs) => {
      if (allSelected) {
        // Add all `result` items to checkedGroupIDs
        const updatedCheckedGroupIDs = [...prevCheckedGroupIDs, value];
        return Array.from(new Set(updatedCheckedGroupIDs)); // Ensure no duplicates
      } else {
        // Remove all `result` items from checkedGroupIDs
        const updatedCheckedGroupIDs = prevCheckedGroupIDs.filter((id) => !(id === value));
        return updatedCheckedGroupIDs;
      }
    });
  };

  const handleColumnReorder = (event: GridColumnReorderEvent) => {
    const sortedColumns = event?.columns?.sort((a: any, b: any) => a.orderIndex - b.orderIndex);

    setStateColumns((prevColumns) => {
      const reorderedColumns = reorderColumns(prevColumns, sortedColumns);
      isSaveLayout && handleSaveLayout?.(dataState, reorderedColumns);
      return [...reorderedColumns];
    });
  };

  const handleRemoveFilter = (item: any) => {
    const newDataState = removeFilterFromState(dataState, item);
    setDataState(newDataState);
    handleSaveLayout?.(newDataState, stateColumns);
  };

  useEffect(() => {
    if (gridConfigFromApi?.columns) {
      setStateColumns(gridConfigFromApi?.columns);
      setShowResetFilter(true);
    }
    if (gridConfigFromApi?.dataState) {
      setDataState(gridConfigFromApi.dataState);
      setShowResetFilter(true);
    }
    if (!gridConfigFromApi?.columns && !gridConfigFromApi?.dataState) {
      setShowResetFilter(false);
    }
  }, [gridConfigFromApi]);

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setDataState({ ...dataState, filter: event.filter });
    isSaveLayout && handleSaveLayout?.({ ...dataState, filter: event.filter }, stateColumns);
  };

  const handlePageChange = (event: GridPageChangeEvent) => {
    setDataState({ ...dataState, skip: event.page.skip, take: event.page.take });
    isSaveLayout && handleSaveLayout?.({ ...dataState, skip: 0, take: event.page.take }, stateColumns);
  };

  const handleSortChange = (event: GridSortChangeEvent) => {
    setDataState({ ...dataState, sort: event.sort });
    isSaveLayout && handleSaveLayout?.({ ...dataState, sort: event.sort }, stateColumns);
  };

  const handleResetGridState = () => {
    handleResetLayout?.();
    setStateColumns(columns.map((col) => ({ ...col, columnShow: true })));
    setDataState({
      take: currentPageSize ? currentPageSize : 50,
      skip: 0,
      group: defaultGroup,
    });
    // navigate(`${location.pathname}?pageSkip=${0}`);
  };

  const safeIsSaveLayout = isSaveLayout ?? false;

  const generateFileName = (ext: string) => {
    const username = localStorage.getItem('USER_NAME') || 'username';

    const capitalizeWords = (str: string) =>
      str
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join('_');

    const formattedFileTitle = capitalizeWords(fileTitle ?? "file");
    const formattedUsername = username.charAt(0).toUpperCase() + username.slice(1);

    return `${formattedFileTitle}_${formattedUsername}_${dayjs().format('MMDDYYYY_HHmmss')}.${ext}`;
  };

  return (
    <div className="gridContainer">
      <LoadingIndicator isLoading={isLoading || isFetching || !!isBusy} isSpinner />
      <ExcelExport ref={_export} fileName={generateFileName('xlsx')}>
        <KendoGrid
          key={
            stateColumns
              .filter((col) => col.field)
              .map((col) => col.field)
              .join('-') || '-'
          }
          className="outerTable"
          {...process(completeData, dataState)}
          {...dataState}
          sortable={true}
          groupable={true}
          resizable={true}
          pageable={{
            pageSizes: [5, 10, 20, 30, 50, 100],
          }}
          selectable={{
            enabled: true,
            mode: selectionMode && selectionMode === 'single' ? 'single' : 'multiple',
            cell: false,
          }}
          data={handleGroupState(process(completeData, dataState))}
          //   data={test}
          detail={DetailsComponent}
          expandField="expanded"
          onExpandChange={expandChange}
          dataItemKey={dataItemKey}
          selectedField={SELECTED_FIELD}
          onDataStateChange={handleDataStateChange}
          //   onSelectionChange={handleSelectionChange}
          onSelectionChange={handleSelectionChangeWithGroup}
          onHeaderSelectionChange={handleHeaderSelectionChange}
          // cells={{
          //   footerCell: footerCell,
          // }}
          rowRender={rowRender}
          cellRender={conditionalCellRender}
          onRowClick={handleRowClick}
          onGroupChange={(e: any) => handleGroupChange(e)}
          reorderable={reorderable}
          onColumnReorder={handleColumnReorder}
          onFilterChange={handleFilterChange}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
        >
          <GridToolbar>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <div>
                <Input onChange={handleSearchDataChange} style={{ width: '400px' }} />

                {safeIsSaveLayout && (
                  <RenderGridFilterLabel
                    state={dataState}
                    displayColumns={stateColumns}
                    closeDefaultFilterFromApi={(item: any) => handleRemoveFilter(item)}
                  />
                )}
              </div>
              <div style={{ width: '110px', display: 'flex', justifyContent: 'flex-end' }}>
                {safeIsSaveLayout && showResetFilter && handleResetLayout && (
                  <Tooltip title={<Typography variant="body1">Reset Layout</Typography>}>
                    <IconButton className="btn btn-default " onClick={handleResetGridState}>
                      <RestoreOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            </div>
          </GridToolbar>

          {SELECTED_FIELD && <Column headerSelectionValue={headerChecked} field={SELECTED_FIELD} title="Selected" />}
          {stateColumns.map(
            (column, idx) =>
              !column.hide &&
              column.columnShow && (
                <Column
                  key={idx}
                  width={column.width}
                  field={column.field}
                  title={column.title}
                  filter={column.filter}
                  locked={column?.locked}
                  // className={column?.className}
                  columnMenu={
                    !column?.isHideColumnMenu
                      ? (props: any) => (
                        <ColumnMenu {...props} columns={stateColumns} onColumnsSubmit={onColumnsSubmit} />
                      )
                      : undefined
                  }
                  cell={CustomCellSelector(column, {
                    onView: handleViewClick,
                    customCell,
                    onEditComplete: handleEditComplete,
                  })}
                  footerCell={!column.isFooterCell ? undefined : footerCell}
                >
                  {/* Render children columns if present */}
                  {column?.children?.map((child, childIdx) => (
                    <Column
                      key={`${idx}-${childIdx}`} // Unique key for child columns
                      width={child.width}
                      field={child.field}
                      // locked={true}

                      title={child.title}
                      filter={child.filter}
                      columnMenu={(props: any) => (
                        <ColumnMenu {...props} columns={stateColumns} onColumnsSubmit={onColumnsSubmit} />
                      )}
                      cell={CustomCellSelector(child, {
                        onView: handleViewClick,
                        customCell,
                        onEditComplete: handleEditComplete,
                      })}
                      footerCell={!child.isFooterCell ? undefined : footerCell}
                    />
                  ))}
                </Column>
              )
          )}
          {isActionColumnVisible && (
            <Column
              filterable={false}
              locked={true}
              cell={(props: GridCellProps) => (
                <GridCommandCell
                  gridCellProps={props}
                  onEditClick={onEdit}
                  onViewClick={onView}
                  onDeleteClick={onDelete}
                  onDownloadClick={onDownload}
                  CustomComponent={CustomComponent}
                  onDeleted={handleDeleteComplete}
                  deleteMutationConfig={deleteMutationConfig}
                  actionPermission={actionPermissions}
                />
              )}
              title="Options"
              className="actionWidth"
              width={optionWidth ?? '150px'}
            />
          )}
        </KendoGrid>
      </ExcelExport>
      <GridPDFExport ref={(pdfExport) => (gridPDFExport = pdfExport)} margin="1cm" scale={0.467} paperSize="A3" fileName={generateFileName('pdf')}>
        <KendoGrid
          className="outerTable"
          {...process(completeData, dataState)}
          {...dataState}
          sortable={true}
          groupable={true}
          resizable={true}
          pageable={{
            pageSizes: [5, 10, 20, 30, 50, 100],
          }}
          selectable={{
            enabled: true,
            mode: selectionMode && selectionMode === 'single' ? 'single' : 'multiple',
            cell: false,
          }}
          data={handleGroupState(process(completeData, dataState))}
          detail={DetailsComponent}
          expandField="expanded"
          onExpandChange={expandChange}
          dataItemKey={dataItemKey}
          selectedField={SELECTED_FIELD}
          onDataStateChange={handleDataStateChange}
          onSelectionChange={handleSelectionChange}
          onHeaderSelectionChange={handleHeaderSelectionChange}
          // cells={{
          //   footerCell: footerCell,
          // }}
          rowRender={rowRender}
          cellRender={conditionalCellRender}
          onRowClick={handleRowClick}
        >
          <GridToolbar>
            <Input onChange={handleSearchDataChange} style={{ width: '400px' }} />
          </GridToolbar>

          {SELECTED_FIELD && <Column field={SELECTED_FIELD} title="Selected" />}
          {stateColumns.map(
            (column, idx) =>
              !column.hide && (
                <Column
                  key={idx}
                  width={column.width}
                  field={column.field}
                  title={column.title}
                  filter={column.filter}
                  footerCell={!column.isFooterCell ? undefined : footerCell}
                >
                  {/* Render children columns if present */}
                  {column?.children?.map((child, childIdx) => (
                    <Column
                      key={`${idx}-${childIdx}`} // Unique key for child columns
                      width={child.width}
                      field={child.field}
                      title={child.title}
                      filter={child.filter}
                      columnMenu={(props: any) => (
                        <ColumnMenu {...props} columns={stateColumns} onColumnsSubmit={onColumnsSubmit} />
                      )}
                      footerCell={!child.isFooterCell ? undefined : footerCell}
                    />
                  ))}
                </Column>
              )
          )}
        </KendoGrid>
      </GridPDFExport>
      <GuardedComponent requiredPermissions={actionPermissions?.export ? [actionPermissions.export] : []}>
        <IconButton className="btn-icon btnExcel" onClick={handleExportExcelCick}>
          <Tooltip title={<Typography variant="body1">Export Excel</Typography>}>
            <img src={Icons.Excel} alt="excel" />
          </Tooltip>
        </IconButton>
      </GuardedComponent>
      {pdfDownload && (
        <GuardedComponent requiredPermissions={actionPermissions?.pdfExport ? [actionPermissions.pdfExport] : []}>
          <IconButton className="btn-icon btnPDF" onClick={handleExportPDFCick}>
            <Tooltip title={<Typography variant="body1">Export PDF</Typography>}>
              <img src={Icons.PDF} alt="PDF" />
            </Tooltip>
          </IconButton>
        </GuardedComponent>
      )}
      {isConfirmation && (
        <ConfirmDialog
          message="Are you sure that you want to remove this service?"
          onClose={handleClose}
          onConfirm={handleConfirm}
          open={isConfirmation}
        />
      )}
    </div>
  );
};
